"use client";

import { CrispChatWidget, Icon, useAppContext } from "@repo/dashboard-core";
import { Crisp } from "crisp-sdk-web";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

export const CRISP_DISMISS_STORAGE_KEY = "tu:crisp-cta:dismissed";

export function CrispFloatingCTA({
  websiteId,
  locale,
  onDismiss,
}: {
  websiteId: string;
  locale: string;
  /** Notify the parent shell so the WhatsApp CTA can
   * reflow into Crisp's spot once it's dismissed (GD-79). */
  onDismiss?: () => void;
}) {
  const t = useTranslations("crisp");
  const { auth } = useAppContext();
  const [mounted, setMounted] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem(CRISP_DISMISS_STORAGE_KEY) === "1");
    setMounted(true);
  }, []);

  // The launcher only shows once Crisp's own script has loaded and the
  // session is ready (session:loaded), not on mount — otherwise the dismiss
  // button renders over empty space while Crisp is still loading (GD-79).
  // chat:initiated fires only once a conversation actually starts, so it's
  // no good here. Already-injected (e.g. client-side nav to a page that
  // mounts this again) means the event already fired once for this tab, so
  // it won't fire again.
  useEffect(() => {
    if (Crisp.isCrispInjected()) {
      setWidgetReady(true);
      return;
    }
    Crisp.session.onLoaded(() => setWidgetReady(true));
    return () => Crisp.session.offLoaded();
  }, []);

  // On mobile Crisp's chat opens full-screen, and the "x" (stacked above
  // Crisp, see below) would float over its send button, where one tap hides
  // Crisp mid-conversation. So it only shows while the chat is closed (GD-116).
  useEffect(() => {
    Crisp.chat.onChatOpened(() => setChatOpen(true));
    Crisp.chat.onChatClosed(() => setChatOpen(false));
    return () => {
      Crisp.chat.offChatOpened();
      Crisp.chat.offChatClosed();
    };
  }, []);

  // Dismissing hides Crisp's launcher, not CrispChatWidget: the widget stays
  // mounted so its logout effect still resets the Crisp session (GD-116).
  // Runs after the widget's own effects (children first), which load Crisp on
  // login and re-show it on a locale change. Before Crisp is injected there
  // is nothing to hide, and chat.hide() would try to load it without an id.
  useEffect(() => {
    if (dismissed && Crisp.isCrispInjected()) Crisp.chat.hide();
  }, [dismissed, locale, auth.isAuthenticated]);

  if (!mounted) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(CRISP_DISMISS_STORAGE_KEY, "1");
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <>
      <CrispChatWidget websiteId={websiteId} locale={locale} />
      {widgetReady && !dismissed && !chatOpen && (
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("dismiss-aria-label")}
          // Above Crisp's launcher (z-index 102, CrispChatWidget) so the button
          // stays clickable, but below Mantine's modals and drawers (200) so an
          // open one covers it along with Crisp (GD-116).
          className="fixed bottom-14 right-4 z-105 flex h-6 w-6 items-center justify-center rounded-full bg-white text-text-secondary shadow-md transition hover:text-text-primary focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </>
  );
}
